import { memo, useCallback, useMemo, useState } from 'react';

import {
  AssetDTO,
  FormElementKind,
  HIGHER_UNIT_READING_MULTIPLIER,
  RecordState,
  shouldWarnLowerUnitReading,
  UnitReadingFormElementAnswerSchema,
  UnitReadingFormElement as UnitReadingFormElementType,
} from '@gearbelt/global';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { match, P } from 'ts-pattern';
import { z } from 'zod';

import { InformationIcon } from '@/components/icons/information';
import WarningIcon from '@/components/icons/warning';
import { AsyncSelect } from '@/components/ui/async-select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input, inputVariants } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getUnitByIdQueryOptions } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { useIsAWA } from '@/utils/awa';

import { UnitReadingConfigLabels } from '../config-field-labels';
import { ConfigLabel } from '../config-label';
import type { FormDesignerForm } from '../form-designer.types';
import { FormFieldError } from '../form-field-error';

export const UnitReadingFormElement = memo(function UnitReadingFormElement({
  asset,
  element,
  value,
  onChange,
  onBlur,
  error,
  readonly,
  lockedNotLatest,
  isEditingExistingRecord = false,
  isTouched,
}: {
  element: UnitReadingFormElementType;
  value?: z.infer<typeof UnitReadingFormElementAnswerSchema>['value'];
  onChange: (value: z.infer<typeof UnitReadingFormElementAnswerSchema>['value']) => void;
  onBlur?: () => void;
  asset: AssetDTO;
  error?: string;
  readonly?: boolean;
  lockedNotLatest?: boolean;
  // On an edit the hard error is suppressed, so the advisory has to cover the disallowed case too or a lowered reading saves silently.
  isEditingExistingRecord?: boolean;
  isTouched?: boolean;
}) {
  const { isAWA } = useIsAWA();

  const { data: units } = useQuery(getUnitByIdQueryOptions({ unitId: element.config?.unitId, isAWA }));

  const unit = useMemo(() => units?.data?.[0], [units]);

  const lastUnitReading = useMemo(
    () => asset.unitReadings?.find((reading) => reading.unitId === element.config?.unitId),
    [asset.unitReadings, element.config?.unitId],
  );

  const hasLastReading = lastUnitReading !== undefined;
  const lastReading = lastUnitReading?.reading ?? 0;

  const highReadingDifference = useMemo(() => {
    // If last reading is 0, don't show warning
    if (lastReading === 0) return false;

    // Warn when the entered reading is well above the last one
    const threshold = lastReading * HIGHER_UNIT_READING_MULTIPLIER;
    return (value || 0) > threshold;
  }, [value, lastReading]);

  const lowerReadingDifference = useMemo(() => value != null && value < lastReading, [value, lastReading]);

  const handleValueChange = useCallback(
    (values: { floatValue?: number }) => {
      onChange(values.floatValue ?? null);
    },
    [onChange],
  );

  const suffix = useMemo(() => (element.config?.unitId ? ` ${unit?.abbreviation || ''}` : ''), [element.config?.unitId, unit?.abbreviation]);

  const isAllowed = useCallback(({ floatValue }: { floatValue?: number }) => floatValue === undefined || floatValue <= 10000000, []);

  const handleBlur = useCallback(() => {
    requestAnimationFrame(() => onBlur?.());
  }, [onBlur]);

  const showLastReading = !error && ((!highReadingDifference && !lowerReadingDifference) || !isTouched) && !readonly;
  const showHighWarning = !error && highReadingDifference && !!isTouched && !readonly;
  const showLowerWarning =
    !error && !!isTouched && !readonly && shouldWarnLowerUnitReading({ value, lastReading, config: element.config, isEditingExistingRecord });

  return (
    <div className="space-y-1">
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <Label required={element.required}>{element.label}</Label>
          {lockedNotLatest && (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="Why can't I edit this reading?" className="text-secondary-text inline-flex">
                    <InformationIcon size={16} />
                  </button>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent side="top" className="bg-default-text max-w-[240px] text-xs text-white">
                    Unit readings can only be edited on the latest schedule check record.
                    <TooltipArrow className="fill-default-text" />
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <NumericFormat
          className={cn(inputVariants({ error: !!error }), 'disabled:bg-grey-1 disabled:opacity-100')}
          value={value ?? ''}
          onValueChange={handleValueChange}
          onBlur={handleBlur}
          placeholder="Enter number..."
          allowNegative={false}
          thousandSeparator=","
          decimalScale={2}
          disabled={readonly}
          suffix={suffix}
          inputMode="decimal"
          isAllowed={isAllowed}
        />
      </div>

      <div className="flex items-center gap-2">
        <span className={cn('flex items-center text-sm', !showLastReading && 'hidden')}>
          Last reading: {hasLastReading ? lastReading.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—'} {unit?.abbreviation}
        </span>
        <span className={cn('flex items-center gap-2 text-sm', !showHighWarning && 'hidden')}>
          <WarningIcon size={20} className="text-default-orange flex-shrink-0" />
          <span>
            Much higher than the last reading: {lastReading.toLocaleString('en-US', { maximumFractionDigits: 2 })} {unit?.abbreviation}
          </span>
        </span>
        <span className={cn('flex items-center gap-2 text-sm', !showLowerWarning && 'hidden')}>
          <WarningIcon size={20} className="text-default-orange flex-shrink-0" />
          <span>
            Lower than last reading {lastReading.toLocaleString('en-US', { maximumFractionDigits: 2 })} {unit?.abbreviation}
          </span>
        </span>
        <FormFieldError error={error} />
      </div>
    </div>
  );
});

export function UnitReadingFormElementConfig({
  element,
  onChange,
}: {
  element: UnitReadingFormElementType;
  onChange?: (updates: Partial<UnitReadingFormElementType>) => void;
}) {
  const form = useFormContext<FormDesignerForm>();
  const formElements = form.watch('elements');

  const usedUnitIds = useMemo(
    () =>
      formElements.flatMap((el) =>
        match(el)
          .with({ kind: FormElementKind.UnitReading, config: { unitId: P.select() } }, (unitId) => [unitId])
          .otherwise(() => []),
      ),
    [formElements],
  );

  const [selectedUnit, setSelectedUnit] = useState<{ label: string; value: string } | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      <ConfigLabel noLabel>
        <span>{UnitReadingConfigLabels.unitId}:</span>
        <AsyncSelect<{ label: string; value: string }>
          defaultOptions
          loadOptions={async (input: string) => {
            const { data } = await (
              await ApiService.GetUnits({
                limit: 100,
                filters: {
                  search: input,
                  state: RecordState.Active,
                },
              })
            ).json();

            if (element.config?.unitId) {
              const initialUnit = data?.find((unit) => unit.id === element.config?.unitId);
              if (initialUnit) {
                setSelectedUnit({ value: initialUnit.id, label: initialUnit.name });
              } else {
                const { data: units } = await (
                  await ApiService.GetUnits({ limit: 1, filters: { fields: { id: [element.config?.unitId] }, state: RecordState.Active } })
                ).json();
                const unit = units?.[0];
                if (unit) {
                  setSelectedUnit({ value: unit.id, label: unit.name });
                }
              }
            }

            return (
              data
                ?.map((unit) => ({
                  value: unit.id,
                  label: unit.name,
                }))
                .filter((unit) => !usedUnitIds.includes(unit.value) || unit.value === element.config?.unitId) ?? []
            );
          }}
          value={selectedUnit}
          onChange={(val) => {
            setSelectedUnit(val);
            onChange?.(val ? { config: { ...element.config, unitId: val.value } } : {});
          }}
          className={cn('w-full')}
          placeholder="Select unit..."
        />
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox
          checked={element.config?.allowLowerReading ?? true}
          onCheckedChange={(checked) => onChange?.({ config: { ...element.config, allowLowerReading: !!checked } })}
        />
        <span>{UnitReadingConfigLabels.allowLowerReading}</span>
      </ConfigLabel>
    </div>
  );
}

export function UnitReadingFormElementMock({ element }: { element: UnitReadingFormElementType }) {
  const [value, setValue] = useState(0);

  const { data: units } = useQuery(getUnitByIdQueryOptions({ unitId: element.config?.unitId }));

  const unit = useMemo(() => units?.data?.[0], [units]);

  return (
    <div className="flex flex-col gap-1">
      <NumericFormat
        className={inputVariants()}
        value={value}
        onValueChange={(values) => setValue(values.floatValue ?? 0)}
        placeholder={`Enter number...`}
        allowNegative={false}
        thousandSeparator=","
        decimalSeparator="."
        inputMode="decimal"
        decimalScale={2}
        disabled={!unit}
        suffix={element.config?.unitId ? ` ${unit?.abbreviation || ''}` : ''}
        isAllowed={({ floatValue }) => floatValue === undefined || floatValue <= 10000000}
      />
      <span className="text-sm">Last reading: 0 {element.config?.unitId ? ` ${unit?.abbreviation || ''}` : ''}</span>
    </div>
  );
}
